/**
 * Clasificación visual de estados de documento / rechazo (puro, sin I/O).
 */

export function extraerMotivoRechazo(comentarios) {
  if (comentarios == null) return null
  const texto = String(comentarios).trim()
  if (!texto) return null
  if (texto.toUpperCase().startsWith('RECHAZADO:')) {
    const motivo = texto.slice('RECHAZADO:'.length).trim()
    return motivo || 'Sin motivo especificado'
  }
  return texto
}

function estadoFirmanteNorm(f) {
  return String(f?.estado || f?.estado_visual || '').trim().toLowerCase()
}

const MENSAJE_PLAZO_VENCIDO =
  'El plazo de 5 días para reenviar este documento ha vencido'

export function clasificarEstadoDocumento(documento) {
  const firmantes = documento?.firmantes || []
  const tieneRechazo =
    documento?.cancelado_por_rechazo === true ||
    firmantes.some((f) => estadoFirmanteNorm(f) === 'rechazado')

  const uiBackend = documento?.estado_ui
    ? String(documento.estado_ui).toLowerCase()
    : ''

  // Backend es fuente de verdad para vencimiento de reenvío
  if (
    uiBackend === 'cancelado_por_vencimiento' ||
    documento?.reenvio_vencido === true
  ) {
    const rechazado = firmantes.find((f) => estadoFirmanteNorm(f) === 'rechazado')
    return {
      estadoUi: 'cancelado_por_vencimiento',
      etiqueta: documento.estado_etiqueta || 'Cancelado por vencimiento',
      canceladoPorRechazo: true,
      reenvioVencido: true,
      puedeReenviar: false,
      mensajePlazo: documento.mensaje_plazo_reenvio || MENSAJE_PLAZO_VENCIDO,
      rechazo: documento.rechazo || (rechazado
        ? {
            nombre: rechazado.nombre || '',
            apellido: rechazado.apellido || '',
            email: rechazado.email || '',
            fecha_rechazo: rechazado.fecha_firma || null,
            motivo: rechazado.motivo_rechazo || extraerMotivoRechazo(rechazado.comentarios) || 'Sin motivo especificado',
          }
        : null),
    }
  }

  // Preferir clasificación del backend si viene completa; si hay firmante
  // rechazado, forzar Rechazado aunque el backend aún envíe en_proceso/pendiente.
  if (documento?.estado_etiqueta && documento?.estado_ui && !tieneRechazo) {
    return {
      estadoUi: (uiBackend === 'cancelado_por_rechazo' ? 'rechazado' : documento.estado_ui),
      etiqueta: documento.estado_etiqueta === 'Cancelado por rechazo' ? 'Rechazado' : documento.estado_etiqueta,
      canceladoPorRechazo: !!documento.cancelado_por_rechazo,
      rechazo: documento.rechazo || null,
      puedeReenviar: documento.puede_reenviar === true,
      reenvioVencido: false,
      mensajePlazo: null,
    }
  }

  // Backend ya envió Rechazado con flags de plazo
  if (
    documento?.estado_etiqueta &&
    (uiBackend === 'rechazado' || uiBackend === 'cancelado_por_rechazo') &&
    tieneRechazo
  ) {
    return {
      estadoUi: 'rechazado',
      etiqueta: documento.estado_etiqueta === 'Cancelado por rechazo' ? 'Rechazado' : documento.estado_etiqueta,
      canceladoPorRechazo: true,
      rechazo: documento.rechazo || null,
      puedeReenviar: documento.puede_reenviar !== false,
      reenvioVencido: false,
      mensajePlazo: null,
    }
  }

  const estado = String(documento?.estado_ui || documento?.estado || '').toLowerCase()

  if (estado === 'completado') {
    return {
      estadoUi: 'completado',
      etiqueta: 'Completado',
      canceladoPorRechazo: false,
      rechazo: null,
      puedeReenviar: false,
      reenvioVencido: false,
      mensajePlazo: null,
    }
  }

  // Cualquier doc con firmante rechazado (aunque el doc siga en_proceso/pendiente)
  if (tieneRechazo || estado === 'rechazado' || estado === 'cancelado_por_rechazo') {
    const rechazado = firmantes.find((f) => estadoFirmanteNorm(f) === 'rechazado')
    const rechazo = documento?.rechazo || (rechazado
      ? {
          nombre: rechazado.nombre || '',
          apellido: rechazado.apellido || '',
          email: rechazado.email || '',
          fecha_rechazo: rechazado.fecha_firma || null,
          motivo: rechazado.motivo_rechazo || extraerMotivoRechazo(rechazado.comentarios) || 'Sin motivo especificado',
        }
      : null)
    return {
      estadoUi: 'rechazado',
      etiqueta: 'Rechazado',
      canceladoPorRechazo: true,
      rechazo,
      puedeReenviar: documento?.puede_reenviar !== false,
      reenvioVencido: false,
      mensajePlazo: null,
    }
  }

  if (estado === 'en_proceso') {
    return {
      estadoUi: 'en_proceso',
      etiqueta: 'En proceso',
      canceladoPorRechazo: false,
      rechazo: null,
      puedeReenviar: false,
      reenvioVencido: false,
      mensajePlazo: null,
    }
  }
  if (estado === 'pendiente') {
    return {
      estadoUi: 'pendiente',
      etiqueta: 'Pendiente',
      canceladoPorRechazo: false,
      rechazo: null,
      puedeReenviar: false,
      reenvioVencido: false,
      mensajePlazo: null,
    }
  }
  if (estado === 'cancelado' || estado === 'cancelado_otra_causa') {
    return {
      estadoUi: 'cancelado_otra_causa',
      etiqueta: 'Cancelado',
      canceladoPorRechazo: false,
      rechazo: null,
      puedeReenviar: false,
      reenvioVencido: false,
      mensajePlazo: null,
    }
  }
  return {
    estadoUi: estado || 'desconocido',
    etiqueta: documento?.estado_etiqueta || estado || 'Desconocido',
    canceladoPorRechazo: false,
    rechazo: null,
    puedeReenviar: false,
    reenvioVencido: false,
    mensajePlazo: null,
  }
}

export function claseBadgeEstado(estadoUi) {
  switch (String(estadoUi || '').toLowerCase()) {
    case 'completado':
      return 'bg-green-100 text-green-800'
    case 'en_proceso':
      return 'bg-amber-100 text-amber-800'
    case 'pendiente':
      return 'bg-yellow-100 text-yellow-800'
    case 'rechazado':
    case 'cancelado_por_rechazo':
      return 'bg-red-100 text-red-800'
    case 'cancelado_por_vencimiento':
      return 'bg-orange-100 text-orange-900'
    case 'cancelado_otra_causa':
    case 'cancelado':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export function formatearNombreRechazo(rechazo) {
  if (!rechazo) return null
  const nombre = [rechazo.nombre, rechazo.apellido].filter(Boolean).join(' ').trim()
  return nombre || rechazo.email || null
}

/** Normaliza estado de firmante para badges (RECHAZADO/rechazado → rechazado). */
export function normalizarEstadoFirmante(estadoOVisual) {
  return String(estadoOVisual || '').trim().toLowerCase()
}

/**
 * Motivo opcional para POST /api/firmar/{token}/rechazar (campo Form `motivo`).
 * Vacío / solo espacios → null (no enviar o dejar que el backend use fallback).
 */
export function motivoRechazoParaEnvio(motivo) {
  const texto = String(motivo ?? '').trim()
  return texto.length > 0 ? texto : null
}

/** FormData listo para el endpoint de rechazo; omite `motivo` si está vacío. */
export function buildRechazoFormData(motivo) {
  const formData = new FormData()
  const texto = motivoRechazoParaEnvio(motivo)
  if (texto) formData.append('motivo', texto)
  return formData
}
